<template>
  <div class="form-page-container">
    <PremiumPage title="Add New Member" subtitle="Register a new house / unit member in the society" showBack>
      <div class="form-section">
        <h3 class="section-title">
          <v-icon small color="var(--brand)" class="mr-2">mdi-home-city-outline</v-icon>
          Unit Configuration
        </h3>

        <div class="half-width-wrap">
          <!-- Unit Type (Mixed society only) -->
          <div v-if="societyUnitType == 3" class="radio-group-wrap">
            <label class="field-label">Unit Type</label>
            <div class="radio-row">
              <label v-for="opt in unitTypeOptions" :key="opt.value" class="radio-option"
                :class="{ active: member.unit_type === opt.value }">
                <input type="radio" :value="opt.value" v-model="member.unit_type" hidden />
                <v-icon small class="mr-1">{{ opt.icon }}</v-icon>
                {{ opt.label }}
              </label>
            </div>
          </div>

          <!-- Block selector -->
          <div v-if="showBlockSelection" class="mt-4">
            <PremiumSelect v-model="member.flat_id" label="Block *" :items="flats" item-text="name" item-value="id"
              placeholder="Select block" icon="mdi-office-building-outline" clearable v-validate="'required'"
              name="flat" data-vv-as="Block" :error="errors.first('flat') || serverSideErrors.flat_id" />
          </div>

          <!-- Member Type (Owner / Tenant) -->
          <div class="radio-group-wrap mt-4">
            <label class="field-label">Member Type</label>
            <div class="radio-row">
              <label v-for="t in membertype" :key="t.value" class="radio-option"
                :class="{ active: member_type === t.value }">
                <input type="radio" :value="t.value" v-model="member_type" hidden />
                {{ t.label }}
              </label>
            </div>
          </div>

          <!-- Unit Number -->
          <div class="mt-4">
            <PremiumSelect v-model="member.house_number" :label="dynamicUnitLabel" :items="availableUnits"
              item-text="label" item-value="value" placeholder="Select unit number" icon="mdi-pound"
              v-validate="'required'" name="HouseNo" data-vv-as="Unit No"
              :error="errors.first('HouseNo') || serverSideErrors.house_number" :loading="unitsLoading" clearable />
          </div>
        </div>
      </div>

      <div class="form-divider" />

      <!-- Section: Member Info -->
      <div class="form-section">
        <h3 class="section-title">
          <v-icon small color="var(--brand)" class="mr-2">mdi-account-outline</v-icon>
          Member Information
        </h3>

        <div class="half-width-wrap">
          <div class="mb-4">
            <PremiumInput v-model="member.name" label="Full Name *" placeholder="Member's full name"
              icon="mdi-account-outline" v-validate="'required'" name="name" data-vv-as="Name"
              :error="errors.first('name') || serverSideErrors.name" />
          </div>

          <div class="mb-4">
            <PremiumInput v-model="member.email" label="Email Address" placeholder="Email (optional)"
              icon="mdi-email-outline" name="email" v-validate="'email'" data-vv-as="Email"
              :error="errors.first('email') || serverSideErrors.email" />
          </div>

          <div class="mb-4">
            <label class="field-label"> Contact No *</label>
            <div class="phone-row">
              <div class="country-code-wrap">
                <vue-country-code @onSelect="onSelect" class="custom-country-code-select" defaultCountry="IN" />
              </div>
              <div class="mobile-input-wrap">
                <PremiumInput v-model="member.mobile_no" label="" placeholder="Enter 10-digit mobile number"
                  icon="mdi-phone-outline" v-validate="'required|max:10|min:10'" name="ContactNo"
                  data-vv-as="Contact No" :error="errors.first('ContactNo') || serverSideErrors.mobileNo
                    " />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer slot for actions -->
      <template #footer>
        <v-btn text class="btn-premium-cancel" @click="$router.go(-1)">Cancel</v-btn>
        <v-btn depressed :loading="loading" class="btn-premium-primary ml-3" @click="saveHouseDetail">
          Submit
        </v-btn>
      </template>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import PremiumPage from "../../common/PremiumPage.vue";

export default {
  name: "AddMember",

  components: { PremiumInput, PremiumSelect, PremiumPage },

  data() {
    return {
      selectedCountryCode: "",
      membertype: [
        { label: "Owner", value: 1 },
        { label: "Tenant", value: 2 },
      ],
      unitTypeOptions: [
        { label: "House", value: 1, icon: "mdi-home-outline" },
        { label: "Shop", value: 2, icon: "mdi-store-outline" },
      ],
      user: {},
      loading: false,
      member: { unit_type: 1 },
      serverSideErrors: {},
      flats: [],
      member_type: 1,
      availableUnits: [],
      unitsLoading: false,
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),
    ...mapState("society", ["hasMultipleBuilding"]),
    ...mapState("theme", ["primaryColor"]),

    brandColor() {
      return this.primaryColor || "#0D9972";
    },
    societyUnitType() {
      return this.unitType;
    },

    dynamicUnitLabel() {
      if (this.societyUnitType == 3) {
        return this.member.unit_type == 2 ? "Shop No *" : "House No *";
      }
      return (this.unitLabel ? this.unitLabel + ' *' : "Unit No *");
    },

    showBlockSelection() {
      if (!this.useBlocks) return false;
      if (this.societyUnitType == 3 && this.member.unit_type == 2) return false;
      return true;
    },
  },

  watch: {
    useBlocks(newVal) {
      if (!newVal) this.member.flat_id = null;
    },
    societyUnitType: {
      immediate: true,
      handler(newVal) {
        if (newVal == 2) this.member.unit_type = 2;
        else this.member.unit_type = 1;
      },
    },
    // Refresh unit list when block changes
    'member.flat_id'() {
      this.getAvailableUnits();
    },
    // For Tenant (member_type 2) show all units including registered ones
    member_type() {
      this.getAvailableUnits();
    },
    // When unit type switches (house ↔ shop), clear selection and refresh
    'member.unit_type'() {
      this.member.house_number = null;
      this.getAvailableUnits();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSelect(event) {
      this.member.country_code = "+" + event.dialCode;
      this.member.countryIso = event.iso2;
    },

    saveHouseDetail() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loading = true;
          const formData = new FormData();
          formData.append("memberType", this.member_type);
          formData.append("name", this.member.name);
          formData.append("email", this.member.email ? this.member.email : "");
          formData.append("societyId", this.user.id);
          formData.append("status", 1);
          formData.append(
            "flatId",
            this.showBlockSelection ? this.member.flat_id : "",
          );
          formData.append("unitType", this.member.unit_type);
          formData.append("mobileNo", this.member.mobile_no);
          formData.append("houseShopNo", this.member.house_number);
          formData.append("countryCode", this.member.country_code || "");
          formData.append("countryIso", this.member.countryIso || "");

          this.$axios
            .post("/societyadmin/members/", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
              this.loading = false;
              this.serverSideErrors = {};
              const data = response.data;
              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                this.$router.push({ path: "/societyadmin/member/list" });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  let newErrors = {};
                  let errorMsg = '';
                  data.errors.forEach((e) => {
                    newErrors[e.field] = e.message;
                    errorMsg += e.message + " ";
                  });
                  this.serverSideErrors = newErrors;
                  this.setAlert({
                    showAlert: true,
                    message: errorMsg.trim(),
                    variant: "red accent-3",
                    timeout: 4000,
                  });
                }
              }
            })
            .catch(() => {
              this.loading = false;
            });
        }
      });
    },

    getFlat() {
      this.$axios.get("societyadmin/flats").then((response) => {
        if (response.data.status) this.flats = response.data.flats;
        else
          this.setAlert({
            showAlert: true,
            variant: "red accent-3",
            timeout: "2000",
            message: response.data.message,
          });
      });
    },

    getAvailableUnits() {
      this.unitsLoading = true;
      const params = {};

      // Pass member_type directly (1 = owner, 2 = tenant)
      if (this.member_type) params.member_type = this.member_type;

      if (this.member.unit_type) params.unit_type = this.member.unit_type;

      const isShopSingleBuilding =
        this.societyUnitType == 3 &&
        this.member &&
        this.member.unit_type == 2 &&
        this.hasMultipleBuilding === 1;

      if (this.member.flat_id && !isShopSingleBuilding) {
        params.flat_id = this.member.flat_id;
      }

      // Edit Member only — include current unit always
      if (this.member && this.member.house_number) {
        params.current_house_number = this.member.house_number;
      }

      this.$axios.get("societyadmin/units/available", { params })
        .then((response) => {
          this.unitsLoading = false;
          if (response.data.status) {
            this.availableUnits = response.data.data.map((u) => ({
              label: u.block_name ? `${u.block_name}-${u.house_number}` : u.house_number,
              value: String(u.house_number),
            }));
          }
        })
        .catch(() => { this.unitsLoading = false; });
    },
  },  // ← closes methods

  mounted() {  // ← now correctly outside methods
    this.user = JSON.parse(localStorage.getItem("user"));
    if (!this.useBlocks) this.selected_type = "single_house";
    this.getFlat();
    this.getAvailableUnits();
  },
};

//   mounted() {
//     this.user = JSON.parse(localStorage.getItem("user"));
//     if (!this.useBlocks) this.selected_type = "single_house";
//     this.getFlat();
//     this.getAvailableUnits();
//   },
// };
</script>

<style scoped>
.form-page-container {
  padding: 24px;
  width: 100%;
  margin: 0 auto;
}

/* ── KEY CHANGE: all fields inside sections are capped at 50% ── */
.half-width-wrap {
  max-width: 50%;
}

@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.form-section {
  padding: 16px;
}

.form-divider {
  height: 1px;
  background: var(--border, #e2e8f0);
  opacity: 0.6;
}

.section-title {
  font-size: 13.5px;
  font-weight: 800;
  color: var(--tx, #111827);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 22px;
  display: flex;
  align-items: center;
}

.field-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  padding-left: 2px;
}

.radio-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  border-radius: 8px;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface2, #fafafa);
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.18s ease;
}

.radio-option:hover {
  border-color: var(--brand, #0d9972);
}

.radio-option.active {
  border-color: var(--brand, #0d9972);
  background: rgba(13, 153, 114, 0.08);
  color: var(--brand, #0d9972);
}

/* ── Inline Phone Row ── */
.phone-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}

.custom-country-code-select {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.custom-country-code-select:hover {
  border-color: var(--brand) !important;
}

.theme--dark .custom-country-code-select {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.mobile-input-wrap {
  flex: 1;
  min-width: 0;
}

.premium-country-wrap-full {
  width: 100%;
}

.custom-country-code-select-full {
  width: 100%;
  height: 46px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
}

::v-deep .custom-country-code-select-full .vue-country-select {
  width: 100%;
  height: 100%;
  border: none !important;
}

::v-deep .custom-country-code-select-full .dropdown-list {
  width: 100% !important;
}

.cancel-btn {
  color: var(--tx2, #374151);
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
}

.save-btn {
  border-radius: 8px !important;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 0 24px !important;
  height: 42px !important;
  text-transform: none !important;
}
</style>
